/**
 * Global help context — drawer + Cmd+K docs search — R-131-SUP-C
 */
// R-131-SUP-C
import React from 'react';
import { useLocation } from 'react-router-dom';
import type { HelpSlug } from '../../lib/helpArticles';
import { HelpArticleDrawer } from './HelpArticleDrawer';
import { HelpDocsCommandPalette } from './HelpDocsCommandPalette';

interface HelpContextValue {
  openDrawer: (slug: HelpSlug) => void;
  closeDrawer: () => void;
  openDocsSearch: () => void;
  closeDocsSearch: () => void;
}

const HelpContext = React.createContext<HelpContextValue | null>(null);

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

export function HelpProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const location = useLocation();
  const [drawerSlug, setDrawerSlug] = React.useState<HelpSlug | null>(null);
  const [docsSearchOpen, setDocsSearchOpen] = React.useState(false);

  const openDrawer = React.useCallback((slug: HelpSlug) => {
    setDrawerSlug(slug);
  }, []);

  const closeDrawer = React.useCallback(() => {
    setDrawerSlug(null);
  }, []);

  const openDocsSearch = React.useCallback(() => {
    setDocsSearchOpen(true);
  }, []);

  const closeDocsSearch = React.useCallback(() => {
    setDocsSearchOpen(false);
  }, []);

  // Cmd+K / Ctrl+K opens help-docs search globally. Template Studio owns
  // Cmd+K on /templates — skip there to avoid fighting SleekTemplateStudio.
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.key.toLowerCase() !== 'k') return;
      if (isEditableTarget(e.target)) return;
      if (location.pathname.startsWith('/templates')) return;

      e.preventDefault();
      setDocsSearchOpen((prev) => !prev);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [location.pathname]);

  const value = React.useMemo<HelpContextValue>(
    () => ({
      openDrawer,
      closeDrawer,
      openDocsSearch,
      closeDocsSearch,
    }),
    [closeDrawer, closeDocsSearch, openDocsSearch, openDrawer],
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpArticleDrawer slug={drawerSlug} onClose={closeDrawer} />
      <HelpDocsCommandPalette
        open={docsSearchOpen}
        onClose={closeDocsSearch}
      />
    </HelpContext.Provider>
  );
}

// No-op fallback used when a component that embeds `ContextualHelpButton`
// renders outside a HelpProvider (e.g. isolated unit tests, print/shared
// routes, or surfaces not wrapped by the global provider). Help is a
// progressive enhancement — its absence must not crash the host surface.
// Module-level constant keeps the reference stable across renders.
const NOOP_HELP: HelpContextValue = {
  openDrawer: () => {},
  closeDrawer: () => {},
  openDocsSearch: () => {},
  closeDocsSearch: () => {},
};

export function useHelp(): HelpContextValue {
  return React.useContext(HelpContext) ?? NOOP_HELP;
}
